// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react'
import {
  createDirectLine,
  createCognitiveServicesSpeechServicesPonyfillFactory
} from 'botframework-webchat'
import FullComposer from 'botframework-webchat/lib/FullComposer'
import type { AddressData, UserData, CustomVoice } from '../../models/types'
import type { ThemeColors } from '../../models/colorTypes'
import { fetchDirectLineToken } from '../../utils/fetchDirectLineToken'
import createCustomStore from '../../utils/createCustomStore'
import SendActivityEventListener from '../Utils/SendActivityEventListener'
import type { Store } from 'redux'
import type { DirectLine } from 'botframework-directlinejs'
import { useSpeech } from '../Contexts/SpeechContext'
import { useRetorik } from '../Contexts/RetorikContext'
import type { WithChildren } from '../../models/utils'
import { useLocaleStore } from '../Contexts/localeStore'
import createAdaptiveCardHostConfig from '../../utils/createAdaptiveCardHostConfig'
import attachmentMiddleware from '../Utils/attachmentMiddleware'
import activityMiddleware from '../Utils/activityMiddleware'
import * as AC from 'adaptivecards/lib/adaptivecards'
import { useRetorikUI } from '../CustomAdaptiveCard'
import MarkdownIt from 'markdown-it'

// Add markdown-it to window for markdown processing
window.markdownit = MarkdownIt

useRetorikUI()

type RetorikComposerProps = WithChildren<{
  addressData: AddressData
  userData: UserData
  externalEventHandler?: (action: any, dispatch?: any) => boolean
  customVoice?: CustomVoice
  height?: number | 'full'
  width?: number | 'full'
  colors: ThemeColors
}>

const defaultUser = {
  name: 'default',
  id: '',
  username: 'default',
  nom: 'defaultName',
  prenom: 'à vous',
  email: '',
  token: '',
  referrer: '',
  ipaddress: ''
}

/**
 * Component using DirectLine
 * @param props
 */
const RetorikComposer = ({
  addressData,
  userData,
  externalEventHandler,
  customVoice,
  children,
  colors
}: RetorikComposerProps): JSX.Element => {
  const [directLine, setDirectLine] = useState<DirectLine | null>(null)
  const [store, setStore] = useState<Store | null>(null)
  const { ponyfillCredentials } = useSpeech()
  const { configuration, agentData, setAppAvailable, loaderClosed } =
    useRetorik()
  const { locale, supported } = useLocaleStore()

  useEffect(() => {
    if (addressData) {
      fetchDirectLineToken(addressData).then((token) => {
        let test = true
        if (configuration.enableConversationCookie !== false) {
          const conversationCookie = document.cookie
            .split('; ')
            .find((row) => row.startsWith('retorikConversationCookie='))
            ?.split('=')[1]

          if (conversationCookie) {
            const splitCookieData = conversationCookie.split('||')
            if (splitCookieData.length === 2) {
              const cookieConversationId = splitCookieData[0]
              const cookieTenantName = splitCookieData[1]
              const fullTenantName = (
                addressData.baseURI ||
                `${addressData.prefix || ''}${addressData.tenant}`
              ).replace(/;|,|\s/g, '')

              if (cookieTenantName === fullTenantName) {
                setDirectLine(
                  createDirectLine({
                    token: token,
                    conversationId: cookieConversationId,
                    watermark: '0'
                  })
                )

                test = false
              }
            }
          }
        }

        test &&
          setDirectLine(
            createDirectLine({
              token: token
            })
          )
      })
    }
  }, [addressData])

  const ponyfillFactory = useMemo(() => {
    if (ponyfillCredentials) {
      const ponyfill = createCognitiveServicesSpeechServicesPonyfillFactory({
        credentials: ponyfillCredentials
      })
      return ponyfill
    }

    return null
  }, [ponyfillCredentials])

  // Initialize the store
  useEffect(() => {
    initStore()
  }, [])

  // Create custom store
  function initStore(): void {
    setStore(
      createCustomStore(
        userData || defaultUser,
        configuration.enableConversationCookie,
        configuration.conversationCookieMaxAge,
        addressData,
        configuration.skipWelcome || false,
        locale,
        externalEventHandler,
        !!configuration.isUsedOnBorne
      )
    )
  }

  const selectVoice = (voices, activity): SpeechSynthesisVoice | null => {
    let voice: SpeechSynthesisVoice | null = null
    // Check if a custom voice is defined in the props
    if (customVoice) {
      if (customVoice.voice) {
        // Search voice with its full name (ex: AriaNeural, GuyNeural)
        const neuralVoice = new RegExp(customVoice.voice, 'iu')
        voice = voices.find(
          ({ name }) => neuralVoice.test(name) || name === customVoice.voice
        )
      } else if (customVoice.gender) {
        // Search voice depending on given gender and locale
        voice = voices.find(({ gender, lang }) => {
          return (
            gender.toLowerCase() === customVoice?.gender?.toLowerCase() &&
            lang === activity.locale
          )
        })
      }
    }
    // Get voice depending on locale and gender
    if (!voice) {
      voice = voices.find(({ gender, lang }) => {
        return (
          gender.toLowerCase() ===
            (agentData?.gender?.toLowerCase() || 'female') &&
          lang === activity.locale
        )
      })
    }
    // No voice found, fall back to US default voices : female => Aria / male => Guy
    if (!voice) {
      const genderedVoice =
        agentData?.gender?.toLowerCase() === 'female'
          ? 'AriaNeural'
          : 'GuyNeural'
      const neuralVoice = new RegExp(genderedVoice, 'iu')
      voice = voices.find(({ name }) => neuralVoice.test(name))
    }
    return voice
  }

  const customAdaptiveCardHostConfig = createAdaptiveCardHostConfig(
    colors.primary,
    colors.card.frame.background
  )

  useEffect(() => {
    if (
      locale &&
      supported &&
      directLine &&
      ponyfillFactory &&
      store &&
      agentData &&
      loaderClosed
    ) {
      setAppAvailable(true)
    } else {
      setAppAvailable(false)
    }
  }, [
    locale,
    supported,
    directLine,
    ponyfillFactory,
    store,
    agentData,
    loaderClosed
  ])

  return directLine && ponyfillFactory && store && agentData ? (
    <FullComposer
      adaptiveCardsHostConfig={customAdaptiveCardHostConfig}
      adaptiveCardsPackage={AC}
      styleOptions={{
        primaryFont: '"Helvetica Neue", sans-serif',
        sendBoxTextWrap: true,
        sendBoxBackground: 'transparent'
      }}
      directLine={directLine}
      webSpeechPonyfillFactory={ponyfillFactory}
      selectVoice={selectVoice}
      store={store}
      locale={locale}
      attachmentMiddleware={attachmentMiddleware}
      activityMiddleware={activityMiddleware}
    >
      <SendActivityEventListener />
      {children}
    </FullComposer>
  ) : (
    <React.Fragment />
  )
}

export default RetorikComposer
